import React, { ReactNode, useEffect, useState } from "react";
import { Trans, useTranslation } from "utilities/i18n";
import { PolyIcon, SubmitButton } from "elements";
import { DISMISS, useNotification } from "collections";
import { format, parseISO } from "date-fns";
import {
  useAccessScreenUrlQuery,
  useRequestUploadDeviceScreenMutation
} from "generated/graphql";
import { DATE_FORMAT, SECOND_IN_MILLISECONDS } from "Constants";

const screenCaptureErrors = {
  NO_SCREENS: "NO_AVAILABLE_SCREENS"
};

export const OVERRIDE_KEY_SEQUENCE = "polylens";

/**
 * Interface to permit getting a screen capture from supported devices.
 * This is only available if the tenant has relays, and the device is connected
 * through a relay.
 *
 * Of course we don't KNOW which relay is responsible for a device, so the mutation
 * that is called sprays requests to all relays, and hopes that one of the relays
 * actually does have the device, and can acquire the screen capture.
 */
export const ScreenCapture: React.FC<{
  deviceId: string;
  tenantId: string;
  connected: boolean;
}> = ({ deviceId, connected, tenantId }) => {
  const { t } = useTranslation();
  const [addNotification] = useNotification();
  const [keyString, setKeyString] = useState<string>("");
  const [override, setOverride] = useState<boolean>(false);

  const [
    { data, fetching },
    requestScreenCapture
  ] = useRequestUploadDeviceScreenMutation();

  // listen for keyboard sequence to override "not connected" status
  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      const sequence = `${keyString}${e.key}`
        .toLowerCase()
        .slice(-OVERRIDE_KEY_SEQUENCE.length);
      setKeyString(sequence);
      if (sequence === OVERRIDE_KEY_SEQUENCE) {
        setOverride(true);
      }
    };

    document.addEventListener("keydown", handleKeydown, false);

    return () => {
      document.removeEventListener("keydown", handleKeydown, false);
    };
  }, [keyString, override]);

  useEffect(() => {
    if (false === data?.requestUploadDeviceScreen?.success) {
      addNotification({
        type: "error",
        dismiss: DISMISS.DEFAULT,
        message: t("SCREEN_CAPTURE_FETCH_ERROR"),
        id: "SCREEN_CAP_MUTATION_ERROR"
      });
    }
    // omit t and addNotification from deps, otherwise causes useEffect to call infinitely with no usefulness
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.requestUploadDeviceScreen]);

  return (
    <div className="diagnostics settings scrollable form grid">
      <div className="card section-title">
        <span className="title pl-1">{t("RETRIEVE_SCREEN_CAPTURE")}</span>
      </div>
      <div className="form-group mb-3">
        <label className="col-3 text-right" />
        <div className="col-9">
          <SubmitButton
            isSubmitting={fetching}
            disabled={!connected && !override}
            labelKey="FETCH_SCREEN_CAPTURE"
            submittingLabelKey="FETCHING"
            onClick={() => {
              requestScreenCapture({ deviceId });
            }}
          />
          {!connected && (
            <div className="col-5 error-message has-error">
              {t("SCREEN_CAPTURE_OFFLINE_TEXT")}
            </div>
          )}
        </div>
      </div>
      {!fetching && <ScreenCaptureDownload deviceId={deviceId} />}
      {fetching && (
        <div className="form-group">
          <div className="col-9">
            <Trans
              className="col-9 italic"
              i18nKey="FETCH_SCREEN_CAPTURE_WAITING"
            >
              <div>
                Attempting to fetch a screen capture from the device.
                <strong className="ml-5">This might take a while.</strong>
              </div>
              The download link will be updated when the screen capture is
              available.
            </Trans>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Sub-component that is responsible for firing the query to get the URL for the
 * screen capture.  (As a sub-component, it can be called conditionally, whereas
 * hooks cannot be called conditionally)
 *
 * pollInterval argument is purely for testing control.
 */
export const ScreenCaptureDownload: React.FC<{
  deviceId: string;
  pollInterval?: number;
}> = ({ deviceId, pollInterval = SECOND_IN_MILLISECONDS }) => {
  const { t } = useTranslation();
  const [addNotification] = useNotification();
  const [fetched, setFetched] = useState<boolean>(true);
  const { data } = useAccessScreenUrlQuery({
    variables: {
      deviceId
    },
    pause: fetched,
    pollInterval,
    requestPolicy: "network-only"
  });

  const ignoreErrorKeys = [screenCaptureErrors.NO_SCREENS];

  // the query should resolve within 7 seconds.  This timeout stops the query if it goes too long.
  useEffect(() => {
    // set this in the useEffect to prevent timing issues with this timeout.
    setFetched(false);
    const timeout = setTimeout(() => {
      if (data?.accessScreenUrl?.success || fetched) {
        return;
      }

      addNotification({
        type: "error",
        dismiss: DISMISS.DEFAULT,
        message: data?.accessScreenUrl?.error
          ? data?.accessScreenUrl.error
          : t("SCREEN_CAPTURE_URL_ERROR"),
        id: "SCREEN_CAP_URL_ERROR"
      });

      setFetched(true);
    }, 8 * SECOND_IN_MILLISECONDS);

    return () => clearTimeout(timeout);
    // omit t and addNotification from deps, otherwise causes useEffect to call infinitely with no usefulness
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // watch for success or error, and stop the query
  useEffect(() => {
    if (data?.accessScreenUrl?.success) {
      setFetched(true);
    }

    // While in progress, no error should stop the query
    if ("UPLOAD_IN_PROGRESS" === data?.accessScreenUrl?.error) {
      return;
    }

    if (data?.accessScreenUrl?.error) {
      if (!ignoreErrorKeys.includes(data?.accessScreenUrl?.error)) {
        addNotification({
          type: "error",
          dismiss: DISMISS.DEFAULT,
          message: data?.accessScreenUrl?.error
            ? data?.accessScreenUrl.error
            : t("SCREEN_CAPTURE_URL_ERROR"),
          id: "SCREEN_CAP_URL_ERROR"
        });
      }

      setFetched(true);
    }
    // omit t and addNotification from deps, otherwise causes useEffect to call infinitely with no usefulness
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.accessScreenUrl?.error, data?.accessScreenUrl?.success]);

  if (screenCaptureErrors.NO_SCREENS === data?.accessScreenUrl?.error) {
    return null;
  }

  if (!fetched) {
    return (
      <ScreenCaptureDownloadTemplate
        downloadLink={<PolyIcon icon="refresh" className="spin loading" />}
        uploadedOnDate={<PolyIcon icon="refresh" className="spin loading" />}
      />
    );
  }

  if (!data?.accessScreenUrl?.success) {
    return (
      <ScreenCaptureDownloadTemplate
        downloadLink={
          <div className="error-message on">
            {t("SCREEN_CAPTURE_URL_ERROR")}
          </div>
        }
        uploadedOnDate={null}
      />
    );
  }

  const downloadUrl = data.accessScreenUrl.url || "";
  const uploadedOnString = data.accessScreenUrl.uploadedOn;
  const uploadedOnFormatted = uploadedOnString
    ? format(parseISO(uploadedOnString), DATE_FORMAT.WITH_TIME_LONG)
    : format(new Date(), DATE_FORMAT.WITH_TIME_LONG);

  let fileName = "";

  if (downloadUrl) {
    const urlArray = downloadUrl.split("?")[0].split("/");
    fileName = urlArray[urlArray.length - 1];
  }

  return (
    <>
      <div className="form-group">
        <label className="col-3 opacity-50 text-right">
          {t("LATEST_SCREEN_CAPTURE")}
        </label>
        <div className="col-9 image-container">
          <img src={downloadUrl} alt={t("SCREEN_CAPTURE")} />
        </div>
      </div>
      <ScreenCaptureDownloadTemplate
        downloadLink={
          <a href={downloadUrl} className="font-sm download-link" download>
            {fileName}
            <PolyIcon icon="download_arrow" className="pl-5" />
          </a>
        }
        uploadedOnDate={uploadedOnFormatted}
      />
    </>
  );
};

/**
 * Three things use the same layout with slightly different content, so
 * use this template to render them.
 *
 * downloadLink may be a spinner icon, an error message, or the link to download the image.
 * uploadedOnDate may be a spinner, empty, or the date the screen capture was created / uploaded.
 */
const ScreenCaptureDownloadTemplate: React.FC<{
  downloadLink: ReactNode;
  uploadedOnDate: ReactNode;
}> = ({ downloadLink, uploadedOnDate }) => {
  const { t } = useTranslation();

  return (
    <>
      <div className="form-group">
        <label className="col-3 opacity-50 text-right">{t("DOWNLOAD")}</label>
        <div className="col-5 link-group pb-5">{downloadLink}</div>
      </div>
      <div className="form-group">
        <label className="col-3 opacity-50 text-right">
          {t("SCREEN_CAPTURED_AT")}
        </label>
        <div className="col-5 pb-5">{uploadedOnDate}</div>
      </div>
    </>
  );
};
